import React from 'react';
import { 
  Mail, MessageSquare, ArrowRight, Eye, EyeOff, ChevronRight, 
  UserCircle, User, Lock 
} from 'lucide-react';

const PartnerLoginForm = ({
  authStates,
  authHandlers
}) => {
  const {
    phone, setPhone,
    password, setPassword,
    isLoading,
    showPassword, setShowPassword,
    category, setCategory,
    isOtpSelectionView, setIsOtpSelectionView,
    isEmailOtpView, setIsEmailOtpView,
    isSmsOtpView, setIsSmsOtpView,
    email, setEmail,
    smsPhone, setSmsPhone,
    otpSent, setOtpSent,
    smsOtpSent, setSmsOtpSent,
    emailOtp,
    smsOtp,
    otpIdentifier, setOtpIdentifier,
    isForgotPassword, setIsForgotPassword,
    forgotPasswordMethod, setForgotPasswordMethod,
    newPassword, setNewPassword,
    confirmPassword, setConfirmPassword,
    showNewPasswordForm
  } = authStates;

  const {
    handleLogin,
    handleEmailLogin,
    handleSmsLogin,
    handleSendOtp,
    handleVerifyEmailOtp,
    handleVerifySmsOtp,
    resetPassword,
    resetToLogin,
    handleEmailOtpChange,
    handleSmsOtpChange,
    navigate
  } = authHandlers;

  return (
    <div className="w-full max-w-md bg-white shadow-2xl rounded-3xl p-5 md:p-6 text-gray-900 border border-gray-100 flex flex-col mx-auto animate-slide-in-right">
      
      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-6 shrink-0">
        <button type="button" className="flex-1 py-2.5 text-center font-bold text-[#067978] border-b-2 border-[#067978] cursor-pointer">
          Login
        </button>
        <button 
          type="button" 
          onClick={() => navigate('/signup')} 
          className="flex-1 py-2.5 text-center font-semibold text-gray-500 hover:text-gray-700 transition-colors cursor-pointer"
        >
          Register
        </button>
      </div>

      <div className="min-h-[380px] flex flex-col justify-between">
        {/* 0. Unified OTP Input View */}
        {isOtpSelectionView && !isEmailOtpView && !isSmsOtpView && !isForgotPassword && (
          <div className="space-y-4 flex-1 flex flex-col animate-fade-in-up">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Login Now</h2>
            </div>

            <div className="space-y-3">
              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-gray-800 ml-1">Email or Mobile Number</label>
                <div className="bg-white/60 border border-gray-200 rounded-xl px-4 py-2.5 focus-within:ring-2 focus-within:ring-[#067978] focus-within:border-transparent transition-all flex items-center">
                  <input
                    type="text"
                    value={otpIdentifier}
                    onChange={(e) => setOtpIdentifier(e.target.value)}
                    className="w-full bg-transparent text-gray-900 font-medium text-base outline-none placeholder:text-gray-400"
                    placeholder="user@example.com"
                  />
                </div>
              </div>

              <button
                type="button"
                disabled={isLoading}
                onClick={handleSendOtp}
                className="w-full py-3 bg-[#067978] hover:bg-[#05605f] text-white font-bold text-base rounded-xl shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 cursor-pointer"
              >
                {isLoading ? 'Sending OTP...' : 'Request for OTP'}
              </button>

              <div className="relative flex py-2 items-center">
                <div className="flex-grow border-t border-gray-200"></div>
                <span className="flex-shrink-0 mx-4 text-gray-400 text-xs font-semibold uppercase tracking-wider">Or</span>
                <div className="flex-grow border-t border-gray-200"></div>
              </div>

              <button
                type="button"
                onClick={() => setIsOtpSelectionView(false)}
                className="w-full py-2.5 border-2 border-[#067978]/20 text-[#067978] font-bold rounded-xl hover:bg-[#067978]/5 transition-all text-base bg-white/50 cursor-pointer"
              >
                Login with Password
              </button>
            </div>

            <p className="text-center text-sm text-gray-600 font-medium mt-auto pt-4">
              Don't have an account?
              <button type="button" onClick={() => navigate('/signup')} className="text-[#067978] font-bold ml-1 hover:underline cursor-pointer">Register Now</button>
            </p>
          </div>
        )}

        {/* 1. Main Login Form (Password) */}
        {!isOtpSelectionView && !isEmailOtpView && !isSmsOtpView && !isForgotPassword && (
          <div className="space-y-4 flex-1 flex flex-col">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Login Now</h2>
            </div>

            <form onSubmit={handleLogin} className="space-y-4">
              {/* Partner Category Selector */}
              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-gray-800 ml-1">Partner Category</label>
                <div className="bg-white/60 border border-gray-200 rounded-xl px-4 py-2.5 focus-within:ring-2 focus-within:ring-[#067978] focus-within:border-transparent transition-all flex items-center group">
                  <UserCircle size={18} className="text-gray-400 group-focus-within:text-[#067978] mr-3 transition-colors flex-shrink-0" />
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="w-full bg-transparent text-gray-900 font-medium text-base outline-none cursor-pointer"
                  >
                    <option value="Restaurant">Restaurant</option>
                    <option value="Shop">Shop</option>
                    <option value="Activities">Activities</option>
                  </select>
                </div>
              </div>

              {/* Phone Input */}
              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-gray-800 ml-1">Registered Phone</label>
                <div className="bg-white/60 border border-gray-200 rounded-xl px-4 py-2.5 focus-within:ring-2 focus-within:ring-[#067978] focus-within:border-transparent transition-all flex items-center group">
                  <User size={18} className="text-gray-400 group-focus-within:text-[#067978] mr-3 transition-colors flex-shrink-0" />
                  <input 
                    type="tel" 
                    value={phone} 
                    onChange={(e) => setPhone(e.target.value)} 
                    className="w-full bg-transparent text-gray-900 font-medium text-base outline-none placeholder:text-gray-400" 
                    placeholder="Enter registered phone" 
                  />
                </div>
              </div>

              {/* Password Input */}
              <div className="space-y-1.5">
                <div className="flex justify-between items-center px-1">
                  <label className="text-sm font-semibold text-gray-800">Password</label>
                  <button type="button" onClick={() => setIsForgotPassword(true)} className="text-[#067978] text-xs font-bold hover:underline cursor-pointer">Forgot?</button>
                </div>
                <div className="bg-white/60 border border-gray-200 rounded-xl px-4 py-2.5 focus-within:ring-2 focus-within:ring-[#067978] focus-within:border-transparent transition-all flex items-center group relative">
                  <Lock size={18} className="text-gray-400 group-focus-within:text-[#067978] mr-3 transition-colors flex-shrink-0" />
                  <input 
                    type={showPassword ? "text" : "password"} 
                    value={password} 
                    onChange={(e) => setPassword(e.target.value)} 
                    className="w-full bg-transparent text-gray-900 font-medium text-base outline-none placeholder:text-gray-400 pr-8" 
                    placeholder="Enter password" 
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#067978] transition-colors cursor-pointer">
                    {showPassword ? <Eye size={18} /> : <EyeOff size={18} />}
                  </button>
                </div>
              </div>

              {/* Submit Button */}
              <button 
                type="submit" 
                disabled={isLoading} 
                className="w-full py-3 bg-[#067978] hover:bg-[#05605f] text-white font-bold text-lg rounded-xl shadow-[0_8px_20px_rgba(6,121,120,0.25)] hover:shadow-[0_8px_25px_rgba(6,121,120,0.4)] hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-center gap-2 mt-2 cursor-pointer"
              >
                {isLoading ? 'Authenticating...' : 'Login'}
              </button>

              <div className="relative flex py-3 items-center">
                <div className="flex-grow border-t border-gray-200"></div>
                <span className="flex-shrink-0 mx-4 text-gray-400 text-xs font-semibold uppercase tracking-wider">Or</span>
                <div className="flex-grow border-t border-gray-200"></div>
              </div>

              <button
                type="button"
                onClick={() => setIsOtpSelectionView(true)}
                className="w-full py-3 border-2 border-[#067978]/20 text-[#067978] font-bold rounded-xl hover:bg-[#067978]/5 hover:border-[#067978]/40 transition-all text-base bg-white/50 cursor-pointer"
              >
                Login with OTP
              </button>
            </form>

            <p className="text-center text-sm text-gray-600 font-medium mt-auto pt-4">
              Don't have an account?
              <button type="button" onClick={() => navigate('/signup')} className="text-[#067978] font-bold ml-1 hover:underline cursor-pointer">Register Now</button>
            </p>
          </div>
        )}

        {/* 2. Email OTP Flow */}
        {isEmailOtpView && !showNewPasswordForm && (
          <div className="space-y-6 flex-1 flex flex-col justify-center">
            <button onClick={resetToLogin} className="text-gray-600 hover:text-[#067978] text-sm flex items-center gap-1 font-bold transition-colors cursor-pointer">
              <ChevronRight className="rotate-180" size={16} /> Back
            </button>
            {!otpSent ? (
              <div className="text-center py-4 space-y-4">
                <div className="w-16 h-16 bg-[#067978]/10 rounded-full flex items-center justify-center mx-auto mb-2 text-[#067978]">
                  <Mail size={32} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Email Verification</h3>
                  <p className="text-gray-600 mt-1 text-sm">We'll send a code to your registered email</p>
                </div>
                <div className="bg-white/60 border border-gray-200 rounded-xl px-4 py-2.5 focus-within:ring-2 focus-within:ring-[#067978]">
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full bg-transparent text-gray-900 font-medium text-base outline-none placeholder:text-gray-400" placeholder="your@email.com" />
                </div>
                <button onClick={handleEmailLogin} disabled={isLoading} className="w-full py-3.5 bg-[#067978] hover:bg-[#05605f] text-white font-bold text-base rounded-xl shadow-lg transition-all cursor-pointer">
                  {isLoading ? 'Sending...' : 'Send Verification Code'}
                </button>
              </div>
            ) : (
              <div className="space-y-6 text-center py-4">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">Verify Email</h3>
                  <p className="text-gray-600 mt-2 text-sm">Enter the code sent to {email}</p>
                </div>
                <div className="flex gap-2 justify-center">
                  {emailOtp.map((digit, i) => (
                    <input key={i} type="tel" name={`email-otp-${i}`} maxLength="1" value={digit} onChange={(e) => handleEmailOtpChange(i, e.target.value)} className="w-10 h-10 md:w-12 md:h-12 bg-white/80 border-2 border-gray-200 rounded-xl text-center text-xl font-bold text-[#067978] focus:border-[#067978] outline-none transition-all shadow-sm" />
                  ))}
                </div>
                <button onClick={handleVerifyEmailOtp} disabled={isLoading} className="w-full py-3.5 bg-[#067978] hover:bg-[#05605f] text-white font-bold text-base rounded-xl shadow-lg transition-all cursor-pointer">Verify & Login</button>
              </div>
            )}
          </div>
        )}

        {/* 3. SMS OTP Flow */}
        {isSmsOtpView && !showNewPasswordForm && (
          <div className="space-y-6 flex-1 flex flex-col justify-center">
            <button onClick={resetToLogin} className="text-gray-600 hover:text-[#067978] text-sm flex items-center gap-1 font-bold transition-colors cursor-pointer">
              <ChevronRight className="rotate-180" size={16} /> Back
            </button>
            {!smsOtpSent ? (
              <div className="text-center py-4 space-y-4">
                <div className="w-16 h-16 bg-[#067978]/10 rounded-full flex items-center justify-center mx-auto mb-2 text-[#067978]">
                  <MessageSquare size={32} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">SMS Verification</h3>
                  <p className="text-gray-600 mt-1 text-sm">Enter your 10-digit mobile number</p>
                </div>
                <div className="bg-white/60 border border-gray-200 rounded-xl px-4 py-2.5 flex items-center focus-within:ring-2 focus-within:ring-[#067978]">
                  <span className="text-gray-500 font-semibold text-base mr-2 flex-shrink-0">+91</span>
                  <input type="tel" value={smsPhone} onChange={(e) => setSmsPhone(e.target.value)} className="w-full bg-transparent text-gray-900 font-medium text-base outline-none placeholder:text-gray-400" placeholder="9876543210" />
                </div>
                <button onClick={handleSmsLogin} disabled={isLoading} className="w-full py-3.5 bg-[#067978] hover:bg-[#05605f] text-white font-bold text-base rounded-xl shadow-lg transition-all cursor-pointer">
                  {isLoading ? 'Sending...' : 'Send SMS OTP'}
                </button>
              </div>
            ) : (
              <div className="space-y-6 text-center py-4">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">Verify Mobile</h3>
                  <p className="text-gray-600 mt-2 text-sm">Enter the code sent to +91 {smsPhone}</p>
                </div>
                <div className="flex gap-2 justify-center">
                  {smsOtp.map((digit, i) => (
                    <input key={i} type="tel" name={`sms-otp-${i}`} maxLength="1" value={digit} onChange={(e) => handleSmsOtpChange(i, e.target.value)} className="w-10 h-10 md:w-12 md:h-12 bg-white/80 border-2 border-gray-200 rounded-xl text-center text-xl font-bold text-[#067978] focus:border-[#067978] outline-none transition-all shadow-sm" />
                  ))}
                </div>
                <button onClick={handleVerifySmsOtp} disabled={isLoading} className="w-full py-3.5 bg-[#067978] hover:bg-[#05605f] text-white font-bold text-base rounded-xl shadow-lg transition-all cursor-pointer">Verify & Login</button>
              </div>
            )}
          </div>
        )}

        {/* 4. Forgot Password Flow */}
        {isForgotPassword && !forgotPasswordMethod && (
          <div className="space-y-6 flex-1 flex flex-col justify-center">
            <button onClick={resetToLogin} className="text-gray-600 hover:text-[#067978] text-sm flex items-center gap-1 font-bold transition-colors cursor-pointer">
              <ChevronRight className="rotate-180" size={16} /> Back
            </button>
            <h2 className="text-2xl font-bold text-gray-900">Reset Password</h2>
            <div className="space-y-3">
              <button onClick={() => { setForgotPasswordMethod('email'); setIsEmailOtpView(true); setOtpSent(false); }} className="w-full p-4 border border-gray-200 rounded-xl flex items-center gap-3 hover:bg-gray-50 transition-colors text-left bg-white/50 cursor-pointer">
                <div className="bg-blue-50 p-2.5 rounded-lg text-blue-600"><Mail size={20} /></div>
                <div><div className="font-semibold text-gray-900 text-sm">Email Recovery</div><div className="text-xs text-gray-500">Send code to registered email</div></div>
              </button>
              <button onClick={() => { setForgotPasswordMethod('sms'); setIsSmsOtpView(true); setSmsOtpSent(false); }} className="w-full p-4 border border-gray-200 rounded-xl flex items-center gap-3 hover:bg-gray-50 transition-colors text-left bg-white/50 cursor-pointer">
                <div className="bg-green-50 p-2.5 rounded-lg text-green-600"><MessageSquare size={20} /></div>
                <div><div className="font-semibold text-gray-900 text-sm">SMS Recovery</div><div className="text-xs text-gray-500">Send code to registered phone</div></div>
              </button>
            </div>
          </div>
        )}

        {/* 5. Set New Password Flow */}
        {showNewPasswordForm && (
          <div className="space-y-6 flex-1 flex flex-col justify-center">
            <h2 className="text-2xl font-bold text-gray-900">Set New Password</h2>
            <div className="space-y-4">
              <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="w-full bg-white/60 border rounded-xl px-4 py-3 outline-none focus:border-[#067978] text-gray-900" placeholder="New Password" />
              <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="w-full bg-white/60 border rounded-xl px-4 py-3 outline-none focus:border-[#067978] text-gray-900" placeholder="Confirm Password" />
            </div>
            <button onClick={resetPassword} disabled={isLoading} className="w-full py-3.5 bg-[#067978] hover:bg-[#05605f] text-white font-bold rounded-xl shadow-lg cursor-pointer">Update Password</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PartnerLoginForm;
